// Centralised JSON-LD builders for SEO/AEO. Each returns a plain object that
// can be passed to <JsonLd />.

export const SITE_URL =
  typeof window !== "undefined" ? window.location.origin : "https://lexworkspace.com";

const ORG = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "LexWorkspace",
  url: SITE_URL,
  description:
    "Promotional hub for StenoPaceMaster (stenography speed-building) and DraftMaster.ai (AI legal drafting).",
  sameAs: ["https://stenopacemaster.com", "https://draftmaster.ai"],
};

export function organizationSchema() {
  return ORG;
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      ORG,
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: "LexWorkspace",
        publisher: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en-US",
      },
    ],
  };
}

export function softwareApplicationSchema({
  name,
  url,
  description,
  category = "BusinessApplication",
  operatingSystem = "Web",
  audience,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    url,
    description,
    applicationCategory: category,
    operatingSystem,
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: "USD",
      price: "0",
      availability: "https://schema.org/InStock",
    },
    ...(audience && {
      audience: { "@type": "Audience", audienceType: audience },
    }),
  };
}

export function faqPageSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function articleSchema({
  title,
  description,
  image,
  url,
  datePublished = "2026-01-01",
  author = "LexWorkspace Editorial",
  product,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image,
    author: { "@type": "Organization", name: author },
    publisher: ORG,
    datePublished,
    dateModified: datePublished,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    about: product === "steno" ? "Stenography Training" : "AI Legal Drafting",
  };
}

export function localBusinessSchema({
  city,
  state,
  url,
  product,
  productUrl,
}) {
  const name =
    product === "steno"
      ? `StenoPaceMaster — Stenography Training in ${city}`
      : `DraftMaster.ai — AI Legal Drafting in ${city}`;
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name,
    url,
    description:
      product === "steno"
        ? `Online stenography speed-building, certification practice and realtime drills serving court reporters, captioners and CART providers in ${city}.`
        : `AI-powered legal drafting and contract automation serving attorneys, paralegals and corporate legal teams in ${city}.`,
    areaServed: {
      "@type": "City",
      name: city,
      ...(state && { containedInPlace: { "@type": "State", name: state } }),
    },
    provider: {
      "@type": "Organization",
      name: product === "steno" ? "StenoPaceMaster" : "DraftMaster.ai",
      url: productUrl,
    },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function itemListSchema(items, listName) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: it.url,
      name: it.name,
    })),
  };
}

export function blogSchema(posts) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${SITE_URL}/blog#blog`,
    name: "LexWorkspace Insights",
    description:
      "Practical writing on stenography speed-building, court-reporting certification prep, AI legal drafting, and contract automation.",
    publisher: ORG,
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.excerpt,
      image: p.image,
      url: `${SITE_URL}/blog/${p.slug}`,
      datePublished: p.datePublished || "2026-01-01",
      author: { "@type": "Organization", name: "LexWorkspace Editorial" },
    })),
  };
}

export function speakableSchema(cssSelectors = ["h1", "p", ".faq-question"]) {
  return {
    "@context": "https://schema.org",
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors,
  };
}
